import { ArrowRight, Building2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { type GovernanceUnitNode } from "~/api/types";
import { useGovernanceUnits } from "~/api/v2/useGovernanceUnits";
import { Button } from "~/atoms/button";
import { Popover, PopoverContent, PopoverTrigger } from "~/atoms/popover";
import { Separator } from "~/atoms/separator";
import { featureFlags } from "~/config/featureFlags";
import { useCanManageGovernanceUnits } from "~/hooks/useCanManageGovernanceUnits";
import {
  getSelectedGovernanceUnitId,
  homeAndDescendants,
  homeGovernanceUnit,
  onGovernanceUnitChangedElsewhere,
  reloadForGovernanceUnitSwitch,
  switchGovernanceUnit,
} from "~/helpers/governanceUnit";
import { cn } from "~/lib/utils";
import { GovernanceUnitList } from "~/molecules/GovernanceUnitList";

export function GovernanceUnitSwitcher() {
  const isEnabled = useFeatureFlagEnabled(featureFlags.governanceUnits);

  // With the switcher off, a selection left over from when it was on must not
  // keep scoping requests to another unit. `undefined` means the flags have
  // not loaded yet. `false` is only trustworthy because flags are never loaded
  // without the `customer` they are targeted on (see config/posthog).
  useEffect(() => {
    if (isEnabled === false && getSelectedGovernanceUnitId() !== null) {
      switchGovernanceUnit(null);
    }
  }, [isEnabled]);

  // A switch in another tab must not leave this one showing the old unit.
  useEffect(
    () => onGovernanceUnitChangedElsewhere(reloadForGovernanceUnitSwitch),
    [],
  );

  return isEnabled ? <GovernanceUnitMenu /> : null;
}

function GovernanceUnitMenu() {
  const { data: tree } = useGovernanceUnits();

  return tree ? <GovernanceUnitPicker tree={tree} /> : null;
}

function GovernanceUnitPicker({ tree }: { tree: GovernanceUnitNode[] }) {
  const { t } = useTranslation();
  const canManageGovernanceUnits = useCanManageGovernanceUnits();
  const [isOpen, setIsOpen] = useState(false);

  const units = homeAndDescendants(tree);
  const home = homeGovernanceUnit(tree);
  const selectedId = getSelectedGovernanceUnitId();
  const acting =
    selectedId === null ? home : units.find((unit) => unit.id === selectedId);

  // A selection the tree no longer has would keep scoping every request to a
  // unit the user cannot see.
  const isStale = selectedId !== null && !acting;
  useEffect(() => {
    if (isStale) {
      switchGovernanceUnit(null);
    }
  }, [isStale]);

  if (!home || !acting || units.length < 2) {
    return null;
  }

  const isViewingOther = !acting.isHome;

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          // Ghost has no colors of its own, so the warning tint needs no dark
          // mode overrides.
          variant={isViewingOther ? "ghost" : "outline"}
          className={cn(
            isViewingOther &&
              "border-warning bg-warning/20 hover:bg-warning/30 aria-expanded:bg-warning/30",
          )}
          title={
            isViewingOther
              ? t(($) => $.governanceUnits.viewingOther)
              : t(($) => $.commons.governanceUnit.label)
          }
        >
          <Building2 data-icon="inline-start" />
          {acting.name}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-lg gap-0 p-0">
        <GovernanceUnitList
          root={home}
          selectedId={acting.id}
          onSelect={(id) => switchGovernanceUnit(id === home.id ? null : id)}
        />
        {canManageGovernanceUnits && (
          <>
            <Separator />

            <Button
              asChild
              variant="ghost"
              size="sm"
              className="w-full justify-between"
            >
              <Link
                to="/governance-units"
                // The top bar stays mounted across routes, so the popover
                // must close itself.
                onClick={() => setIsOpen(false)}
              >
                {t(($) => $.governanceUnits.manage)}
                <ArrowRight data-icon="inline-end" />
              </Link>
            </Button>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}
